import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type ILesson from "../models/ILesson";
import type ILessonMetaRequest from "../models/ILessonMetaRequest";

interface LessonState {
  lessonId: number | null;
  lessonVersionId: number | null;
  lessonVersionDisplayableId: number | null;
  currentPageVersionId: number | null;

  name: string;
  description: string;

  lessonData: ILesson | null;
  lessonMeta: ILessonMetaRequest | null;
}

interface LessonActions {
  // Set individual properties
  setLessonId: (id: number | null) => void;
  setLessonVersionId: (id: number | null) => void;
  setLessonVersionDisplayableId: (id: number | null) => void;
  setCurrentPageVersionId: (id: number | null) => void;
  setName: (name: string) => void;
  setDescription: (description: string) => void;

  // Set complete data objects
  setLessonData: (data: ILesson | null) => void;
  setLessonMeta: (meta: ILessonMetaRequest | null) => void;

  // Bulk update from lesson data
  updateFromLessonData: (data: Partial<ILesson>) => void;

  // Reset the entire store
  reset: () => void;
}

/**
 * Combined Lesson Store Type
 */
type LessonStore = LessonState & LessonActions;

/**
 * Initial state for the lesson store
 */
const initialState: LessonState = {
  lessonId: null,
  lessonVersionId: null,
  lessonVersionDisplayableId: null,
  currentPageVersionId: null,
  name: "",
  description: "",
  lessonData: null,
  lessonMeta: null,
};

export const useLessonStore = create<LessonStore>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        ...initialState,

        // Actions
        setLessonId: (id) => set({ lessonId: id }, false, "setLessonId"),

        setLessonVersionId: (id) => set({ lessonVersionId: id }, false, "setLessonVersionId"),

        setLessonVersionDisplayableId: (id) =>
          set({ lessonVersionDisplayableId: id }, false, "setLessonVersionDisplayableId"),

        setCurrentPageVersionId: (id) => set({ currentPageVersionId: id }, false, "setCurrentPageVersionId"),

        setName: (name) => set({ name }, false, "setName"),

        setDescription: (description) => set({ description }, false, "setDescription"),

        setLessonData: (data) => set({ lessonData: data }, false, "setLessonData"),

        setLessonMeta: (meta) => set({ lessonMeta: meta }, false, "setLessonMeta"),

        updateFromLessonData: (data) =>
          set(
            (state) => ({
              lessonId: data.lessonId ?? state.lessonId,
              lessonVersionId: data.lessonVersionId ?? state.lessonVersionId,
              lessonVersionDisplayableId: data.lessonVersionDisplayableId ?? state.lessonVersionDisplayableId,
              name: data.name ?? state.name,
              description: data.description ?? state.description,
              lessonData: data as ILesson,
            }),
            false,
            "updateFromLessonData",
          ),

        reset: () => set(initialState, false, "reset"),
      }),
      {
        name: "lesson-storage", // Key in session storage
        // Only persist essential identifiers and basic info
        partialize: (state) => ({
          lessonId: state.lessonId,
          lessonVersionId: state.lessonVersionId,
          lessonVersionDisplayableId: state.lessonVersionDisplayableId,
          name: state.name,
          description: state.description,
        }),
      },
    ),
    {
      name: "LessonStore", // DevTools name
      enabled: process.env.NODE_ENV === "development",
    },
  ),
);

// Selectors for convenient access
export const lessonSelectors = {
  // Get lesson IDs
  getLessonId: (state: LessonStore) => state.lessonId,
  getLessonVersionId: (state: LessonStore) => state.lessonVersionId,
  getLessonVersionDisplayableId: (state: LessonStore) => state.lessonVersionDisplayableId,
  getCurrentPageVersionId: (state: LessonStore) => state.currentPageVersionId,

  // Get lesson info
  getName: (state: LessonStore) => state.name,
  getDescription: (state: LessonStore) => state.description,

  // Get full objects
  getLessonData: (state: LessonStore) => state.lessonData,
  getLessonMeta: (state: LessonStore) => state.lessonMeta,
};
